import React from 'react'
import Head from 'next/head'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FaWrench } from '@react-icons/all-files/fa/FaWrench'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products'
import { SHOW_POPUP } from '../store/slices/ui'
import { openStageBuilderPopup } from '../components/StageBuilderComp'
import QuotePop from '../components/DeferredQuotePopup'
import ClosingActionBand from '../components/design-system/marketing/ClosingActionBand'
import EquipmentGuide from '../components/design-system/marketing/EquipmentGuide'
import EventShowcase from '../components/design-system/marketing/EventShowcase'
import FAQGroup from '../components/design-system/marketing/FAQGroup'
import MediaFeature from '../components/design-system/marketing/MediaFeature'
import PageShell from '../components/design-system/foundations/PageShell'
import ProductCatalog from '../components/design-system/commerce/ProductCatalog'
import RelatedServices from '../components/design-system/marketing/RelatedServices'
import ReviewProof from '../components/design-system/marketing/ReviewProof'
import ServiceHero from '../components/design-system/marketing/ServiceHero'
import ServiceOverview from '../components/design-system/marketing/ServiceOverview'
import stageHero from '../public/Pictures/categories-v2/stage-rental-hero-v2.jpg'
import stageCorporate from '../public/Pictures/stage-corporate.jpg'
import stageOutdoor from '../public/Pictures/stage-outdoor.jpg'
import stageOutdoorMiamiBeach from '../public/Pictures/stage-outdoor-miami-beach-v2.webp'
import stagePresentationFiu from '../public/Pictures/stage-presentation-fiu-v2.webp'
import stageConcert from '../public/Pictures/Product/stage-rental-miami.jpg'
import stagePrivateEvent from '../public/Pictures/Product/bar-mitzvah-stage.jpg'
import stageCatwalk from '../public/Pictures/stage-catwalk-ballroom-v2.webp'
import completeStageSystem from '../public/Pictures/stage-complete-corporate-town-hall-v2.webp'
import * as gtag from '../lib/gtag'
import { compactCatalogProducts } from '../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../utilities/eventPublicPayload'
import { getEventServices } from '../utilities/portfolioPresentation'
import { requestPageData } from '../utilities/boundedPageData'

const PAGE_PATH = '/stage-rental'
const CANONICAL_URL = `https://miamisoundrental.com${PAGE_PATH}`

const stageSelectionContent = {
  id: 'stage-selection-guide',
  eyebrow: 'Choose the physical stage first',
  heading: 'Match the footprint, height and site conditions to the event',
  body: [
    'A useful stage plan starts with the people and equipment that must fit on the platform—not with a generic package name. We configure modular decks and risers around the required footprint, finished height and venue access.',
    'Sound, lighting, video walls, truss and tents are separate systems. They can be coordinated with the stage when needed without confusing the physical platform with a complete production package.'
  ],
  guide: {
    eyebrow: 'Decision support',
    heading: 'What we confirm before delivery',
    summary: 'The same footprint can require a different supporting plan depending on height, surface and access.',
    body: [
      <p key='footprint'><strong>Footprint and use</strong><br />Allow room for speakers, panels, performers, instruments, DJ furniture or a runway—not only the visible activity at center stage.</p>,
      <p key='height'><strong>Height and audience sightlines</strong><br />Low risers suit presentations and ceremonies; larger rooms and performance programs may need a taller platform, stairs and guardrails.</p>,
      <p key='site'><strong>Surface, access and safety</strong><br />Indoor floors, grass, uneven outdoor sites and constrained load-in paths affect leveling, stairs, skirting and the final delivery plan.</p>
    ]
  },
  media: {
    src: stageCorporate,
    alt: 'Modular stage platform configured for a corporate presentation in South Florida'
  }
}

const outdoorStageContent = {
  id: 'outdoor-stage-rental',
  eyebrow: 'Outdoor modular stage rental',
  heading: 'Outdoor stages are planned around the site—not supplied as trailers',
  body: [
    'We build outdoor stage platforms from modular decks and risers, then confirm the footprint, finished height, surface, leveling, stairs, guardrails and load-in route for the site.',
    'Miami Sound Rental does not offer trailer-based or hydraulic mobile stages. If your search means a stage on wheels, this is not the correct service. If you need a portable modular platform assembled at the venue, compare the packages above or configure the exact size.'
  ],
  guide: {
    heading: 'Outdoor planning checks',
    summary: 'Final approval depends on safe site conditions and a workable weather plan.',
    body: [
      <p key='surface'><strong>Surface and leveling</strong><br />Tell us whether the stage will sit on concrete, pavement, grass or another surface.</p>,
      <p key='weather'><strong>Weather and venue rules</strong><br />Outdoor use is reviewed against forecast, wind, rain exposure and venue requirements before setup.</p>,
      <p key='access'><strong>Load-in and collection</strong><br />Vehicle access, setup windows and safe collection timing are confirmed with the final location.</p>
    ]
  },
  media: {
    src: stageOutdoorMiamiBeach,
    alt: 'Portable modular outdoor stage assembled on Miami Beach with black skirting and sound equipment',
    position: 'center 58%',
    caption: 'Modular outdoor stage platform assembled on Miami Beach.'
  }
}

const stageUseCases = [
  {
    eyebrow: 'Presentations · graduations · ceremonies',
    title: 'Presentation and graduation stages',
    summary: 'Raised platforms that improve audience sightlines without adding unnecessary production systems.',
    body: <p>Choose a compact speaking riser or a wider platform for panels, awards, commencements and school ceremonies. Stairs, rails and skirting are matched to the height and venue.</p>,
    image: stagePresentationFiu,
    imageAlt: 'Modular presentation stage with podium, stairs, seating and sound system in a South Florida conference venue'
  },
  {
    eyebrow: 'Bands · DJs · live programs',
    title: 'Band and concert stages',
    summary: 'Working room for performers, instruments, backline and safe movement.',
    body: <p>Start with the performance footprint. Sound, lighting and video can then be scoped as separate systems when the program needs them.</p>,
    image: stageConcert,
    imageAlt: 'Band performing on a modular outdoor stage in South Florida'
  },
  {
    eyebrow: 'Weddings · parties · private events',
    title: 'Wedding and private-event stages',
    summary: 'Clean, finished platforms for entertainment, ceremonies and focal moments.',
    body: <p>Use low risers for a DJ or ceremony, or a wider stage for live entertainment. Skirting, stairs and access are planned with the room layout.</p>,
    image: stagePrivateEvent,
    imageAlt: 'Finished modular stage used for a private event in a South Florida ballroom'
  },
  {
    eyebrow: 'Runways · fashion shows',
    title: 'Runway and catwalk stages',
    summary: 'Longer modular footprints planned around audience placement and venue access.',
    body: <p>Runway dimensions, stage height, stairs and turning space should be confirmed together. Lighting and show production remain separate specialist services.</p>,
    image: stageCatwalk,
    imageAlt: 'Modular stage platforms arranged at multiple heights for a custom layout'
  },
  {
    eyebrow: 'Outdoor programs',
    title: 'Portable outdoor stage platforms',
    summary: 'Modular decks assembled on site for speeches, bands, community events and ceremonies.',
    body: <p>We review the surface, leveling, weather plan and safe access. These are modular platforms—not trailer-based or hydraulic mobile stages.</p>,
    image: stageOutdoor,
    imageAlt: 'Portable modular stage assembled outdoors in South Florida'
  }
]

const requiredStageQuestions = [
  {
    question: 'What affects stage rental cost?',
    answer: 'Stage rental cost depends on the footprint, finished height, rental duration, stairs, guardrails, skirting, site surface, venue access, delivery location and setup schedule. Choose a package to see equipment pricing, then enter the date and location for the final delivery and labor price.'
  },
  {
    question: 'What is included with a stage rental?',
    answer: 'This page covers the physical modular stage: decks or risers and the supporting components required for the selected layout. Stairs, guardrails and skirting are added when the height and use require them. Sound, lighting, video walls, truss and tents are separate services.'
  },
  {
    question: 'Do you rent trailer-based or hydraulic mobile stages?',
    answer: 'No. Miami Sound Rental offers portable modular stage platforms assembled at the venue. We do not offer stages on wheels, trailer stages or hydraulic mobile stages.'
  },
  {
    question: 'Can a modular stage be installed outdoors?',
    answer: 'Yes, when the site and weather conditions allow safe setup. We review the ground surface, leveling, stairs, guardrails, load-in access, forecast and venue requirements before confirming an outdoor stage.'
  },
  {
    question: 'How do I choose the right stage size?',
    answer: 'List everyone and everything that must fit on the platform, then allow working room and safe movement. Speakers and ceremonies may need a compact riser, while bands, DJs, runways and multi-person programs need a wider or longer footprint.'
  },
  {
    question: 'Are stage platforms and stage risers the same service?',
    answer: 'They use the same modular deck system. A stage riser is usually a smaller or lower platform, while a stage rental may combine more decks into a larger footprint. This page lets you compare both uses without creating competing pages for the same equipment.'
  }
]

const normalizeFaq = questions => {
  const normalized = (Array.isArray(questions) ? questions : [])
    .map(question => ({
      ...question,
      question: question?.question || question?.title || question?.name,
      answer: question?.answer || question?.answerContent || question?.body
    }))
    .filter(question => question.question && question.answer)

  return [...normalized, ...requiredStageQuestions].filter((question, index, array) => (
    array.findIndex(candidate => candidate.question.toLowerCase() === question.question.toLowerCase()) === index
  ))
}

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [landingResponse, productResponse, faqResponse, reviewResponse] = await Promise.all([
    requestPageData({
      url: `${SERVER_IP}/user/static-landing`,
      fallback: {
        newsletterevents: [],
        total_events: null
      },
      label: 'stage rental events'
    }),
    requestPageData({
      required: true,
      validateData: Array.isArray,
      url: `${SERVER_IP}/inventory/product`,
      params: {
        msr: true,
        cat: 'Stage-Bundles'
      },
      label: 'stage rental catalog'
    }),
    requestPageData({
      url: `${SERVER_IP}/faq`,
      params: {
        category: 'Stage'
      },
      fallback: [],
      label: 'stage rental FAQs'
    }),
    requestPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: {
        page: PAGE_PATH,
        limit: 3
      },
      fallback: null,
      label: 'stage rental reviews'
    })
  ])

  const catalogProducts = compactCatalogProducts(productResponse.data)
  store.dispatch({ type: productsReceived.type, payload: catalogProducts })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const { newsletterevents = [], total_events = null } = landingResponse.data || {}
  const usedFallback = [landingResponse, productResponse, faqResponse, reviewResponse]
    .some(response => response.usedFallback)

  return {
    props: {
      faq: faqResponse.data,
      newsletterevents: compactPublicEvents(newsletterevents),
      reviewData: reviewResponse.data,
      total_events
    },
    revalidate: usedFallback ? 60 : 3600
  }
})

const StageRental = ({ faq, newsletterevents = [], reviewData, total_events }) => {
  const dispatch = useDispatch()
  const products = useSelector(getFilteredProducts)
  const stageProducts = products.filter(product => (
    product.categories?.some(category => /staging\/stage bundles/i.test(category))
  ))
  const faqQuestions = normalizeFaq(faq)
  const stageEvents = newsletterevents.filter(event => getEventServices(event).includes('Staging'))

  const openStageBuilder = () => {
    gtag.event({ action: 'stage_builder_click', label: 'leads', value: 2, category: 'orders' })
    openStageBuilderPopup({ dispatch })
  }

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const heroSteps = [
    { icon: 'search', title: 'Choose the footprint', description: 'Compare packages or configure exact dimensions.' },
    { icon: 'calendar', title: 'Select dates', description: 'Tell us when and where the stage is needed.' },
    { icon: 'truck', title: 'Delivery & assembly', description: 'Our crew installs the modular platform on site.' },
    { icon: 'success', title: 'Event ready', description: 'Receive a finished stage with the agreed access and safety components.' }
  ]

  const primaryHeroAction = {
    label: 'Browse Stage Packages',
    href: '#bundles',
    icon: FiShoppingBag,
    conversionKey: 'stage-rental.hero.browse-stages',
    channel: 'rental',
    salesPath: 'self_service',
    focusTargetOnNavigate: true,
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }
  const builderAction = {
    label: 'Configure a Custom Stage',
    icon: FaWrench,
    conversionKey: 'stage-rental.hero.build-stage',
    channel: 'builder',
    salesPath: 'self_service',
    onClick: openStageBuilder
  }
  const quoteAction = {
    label: 'Request a Complete Stage Quote',
    icon: FiMessageCircle,
    conversionKey: 'stage-rental.closing.complete-stage-quote',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Stage Rental in Miami and Fort Lauderdale',
    serviceType: 'Modular stage platform and stage riser rental',
    url: CANONICAL_URL,
    description: 'Modular stage platforms, risers, stairs, guardrails and skirting with delivery and setup in Miami, Fort Lauderdale and South Florida.',
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'AdministrativeArea', name: 'South Florida' }
    ],
    provider: { '@type': 'LocalBusiness', name: 'Miami Sound Rental', url: 'https://miamisoundrental.com' },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Stage rental packages',
      itemListElement: stageProducts
        .filter(product => Number.isFinite(Number(product.price?.rental)))
        .slice(0, 12)
        .map(product => ({
          '@type': 'Offer',
          price: product.price.rental,
          priceCurrency: 'USD',
          itemOffered: { '@type': 'Service', name: product.name, description: product.description }
        }))
    }
  }

  return (
    <>
      <BreadcrumbJsonLd itemListElements={[
        { position: 1, name: 'Home', item: 'https://miamisoundrental.com' },
        { position: 2, name: 'Stage Rental', item: CANONICAL_URL }
      ]} />
      <NextSeo
        title='Stage Rental in Miami & Fort Lauderdale | Modular Stages'
        description='Rent modular stages, stage platforms and risers in Miami and Fort Lauderdale. Compare footprints and heights or configure a custom stage with delivery and setup.'
        canonical={CANONICAL_URL}
        twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
        openGraph={{
          type: 'website',
          url: CANONICAL_URL,
          title: 'Stage Rental in Miami & Fort Lauderdale | Modular Stages',
          description: 'Rent modular stages, stage platforms and risers in Miami and Fort Lauderdale. Compare footprints and heights or configure a custom stage with delivery and setup.',
          images: [{
            url: 'https://miamisoundrental.com/Pictures/categories-v2/stage-rental-hero-v2.jpg',
            width: 1915,
            height: 821,
            alt: 'Portable event stage with overhead truss and professional lighting in Miami'
          }],
          site_name: 'Miami Sound Rental'
        }}
        additionalMetaTags={[{
          name: 'keywords',
          content: 'stage rental Miami, stage rental Fort Lauderdale, modular stage rental, portable stage rental, stage platform rental, stage riser rental, outdoor stage rental'
        }]}
      />
      <Head>
        <script
          key='stage-rental-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>

      <PageShell>
        <ServiceHero
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Stage Rental' }]}
          eyebrow='Modular stage rental · Miami, Fort Lauderdale & South Florida'
          title='Stage Rental in Miami & Fort Lauderdale'
          description='Compare portable modular stages, platforms and risers by footprint and height, or configure a custom stage for presentations, bands, weddings, runways and outdoor events. Delivery and assembly are available.'
          image={{ src: stageHero, position: 'center' }}
          imageAlt='Portable event stage with overhead truss and professional lighting in Miami'
          primaryAction={primaryHeroAction}
          secondaryAction={builderAction}
          processLabel='How stage rental works'
          steps={heroSteps}
        />

        <ProductCatalog
          carousel={{
            name: 'Stage Rentals',
            url: CANONICAL_URL,
            description: 'Portable modular stages, stage platforms and risers for rent'
          }}
          filters={[
            ['Light & Portable', 'IntelliStage'],
            ['Heavy Duty', 'MSR'],
            ['Acrylic', 'Acrylic']
          ]}
          sortOptions={['Capacity', 'Area', 'Height']}
          listName='Stage Packages'
          anchorId='bundles'
          eyebrow='Compare physical stage options'
          description='Choose a ready-made footprint below. If none matches the room or program, configure the exact width, depth, height and finish without leaving the stage-rental path.'
          headerAction={{ ...builderAction, label: 'Build a Custom Footprint' }}
          products={stageProducts}
        />

        <ServiceOverview content={stageSelectionContent} />

        <EquipmentGuide
          eyebrow='Choose by event use'
          headingId='stage-rental-by-event-type'
          title='Stage Rental by Event Type'
          description='The event type suggests a starting footprint, but the number of people, equipment, height, site and access determine the final stage.'
          items={stageUseCases}
        />

        <ServiceOverview content={outdoorStageContent} />

        <MediaFeature
          eyebrow='Complete stage systems'
          heading='Need the stage plus sound, lighting, truss or video?'
          body={(
            <>
              <p>The stage platform should be sized first. The production systems can then be added around the program, audience and venue instead of being bundled into every stage rental by default.</p>
              <p>For a coordinated scope, request a complete stage quote. For one specialist system, use the direct links below so stage, truss, sound, lighting and visuals keep distinct jobs.</p>
            </>
          )}
          secondaryHeading='What remains part of the stage rental'
          secondaryBody={<p>Modular decks, risers, supporting hardware and the stairs, guardrails or skirting required for the confirmed platform.</p>}
          image={completeStageSystem}
          imageAlt='Finished corporate presentation stage with two LED screens, sound system and stairs in a South Florida ballroom'
          imagePosition='center 58%'
          framed
        />

        <RelatedServices
          eyebrow='Add only the systems the event needs'
          heading='Complete the stage without confusing page ownership'
          description='Use these specialist pages when the request extends beyond the physical platform.'
          headingId='stage-related-services'
          additionalServices={[
            { h1: 'Stage Truss Rental', url: '/staging/truss', miniText: 'Plan ground-supported or overhead truss separately from the stage platform.' },
            { h1: 'Pipe & Drape Rental', url: '/staging/drape', miniText: 'Add backdrops, room divisions and finished event boundaries.' },
            { h1: 'Stage Skirting & Floor Protection', url: '/staging/accessories', miniText: 'Match black stage skirting to the deck height and plan approved floor protection separately.' },
            { h1: 'Sound System Rental', url: '/sound-system-rental', miniText: 'Plan audience coverage, microphones, mixers and technical sound support.' },
            { h1: 'Event Lighting Rental', url: '/lighting-rental', miniText: 'Choose stage, room and presentation lighting for the event environment.' },
            { h1: 'Visual Equipment Rental', url: '/visuals', miniText: 'Add video walls, projectors, screens and displays through the visuals family.' }
          ]}
        />

        <FAQGroup
          questions={faqQuestions}
          title='Stage Rental Questions'
          headingId='stage-rental-faq-title'
          eyebrow='Planning and pricing answers'
          intro='Clear answers about stage cost, sizing, outdoor use, included components and the difference between modular platforms and mobile trailer stages.'
        />

        <EventShowcase
          actionLabel='View More Stage Production Work'
          description='These projects show modular stages in the context of real presentations, private events, concerts and outdoor programs.'
          events={stageEvents}
          heading='Stages in real event environments'
          eyebrow='Recent stage work'
          sectionId='recent-stage-events'
          totalEvents={total_events}
        />

        <ReviewProof
          filter={['convenient', 'empower']}
          heading='Recent feedback about delivery, setup and event support'
          initialData={reviewData}
          pagePath={PAGE_PATH}
          sectionId='stage-rental-reviews'
        />

        <ClosingActionBand
          ariaLabel='Stage rental options'
          eyebrow='Choose the stage path that fits'
          heading='Ready to plan the physical stage?'
          description='Configure an exact modular footprint, or ask our production team to coordinate the stage with sound, lighting, truss and video.'
          headingId='stage-rental-closing-action'
          proofItems={[
            'Footprint, height and site conditions reviewed together',
            'Delivery and on-site assembly available across South Florida',
            'Specialist production systems kept separate until requested'
          ]}
          primaryAction={builderAction}
          secondaryAction={quoteAction}
        />
      </PageShell>
    </>
  )
}

export default StageRental
